// Checks to see if either session or local storage is available before storing/getting items from it
// eslint-disable-next-line import/prefer-default-export
import moment from 'moment';
import Case from 'case';
import rename from 'deep-rename-keys';
import getConfig from 'next/config';
import { matchPath } from 'react-router';
import React from 'react';
import logger from '@greenlight/logger';
import countriesAndTimezones from 'countries-and-timezones';

const { publicRuntimeConfig } = getConfig();
const HOST_URL = `${publicRuntimeConfig.webregServiceURL}/api/v1`;
const ACCESS_MANAGEMENT_URL = `${publicRuntimeConfig.webregServiceURL}/growth/api/v2/registration/`;

export function storageAvailable(type) {
  try {
    const storage = window[type];
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    logger.error(`Could not access ${type}`);
  }
}

export function isUSTimezone() {
  let country;
  if (
    window.Intl &&
    typeof Intl.DateTimeFormat().resolvedOptions().timeZone === 'string'
  ) {
    country = countriesAndTimezones.getCountryForTimezone(
      Intl.DateTimeFormat().resolvedOptions().timeZone
    )?.id;
  }
  return !country || country === 'US';
}

export function isValidEmail(email) {
  return (
    email.match(
      // eslint-disable-next-line no-useless-escape
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    ) !== null
  );
}

export function youngerThan18(dob) {
  return moment().diff(moment(dob, 'MM/DD/YYYY'), 'years') < 18;
}

export function isPOBox(streetAddress) {
  const poBoxStrings = [
    'po',
    'p.o. box',
    'p.o.box',
    'post office box',
    'po box',
    'post box',
    'postal box',
    'postal office box',
    'post box no'
  ];
  if (!streetAddress) {
    return false;
  }
  // Replace multiple spaces with single space, convert to lowercase
  const cleanedAddress = streetAddress.replace(/ +/g, ' ').toLowerCase();

  let addressIsPOBox = false;
  poBoxStrings.forEach(poString => {
    // Check if string contains any of the above po box strings followed by a numerical value
    const poRegex = new RegExp(`${poString} [A-Za-z0-9]+`, 'g');
    if (poRegex.test(cleanedAddress)) {
      addressIsPOBox = true;
    }
  });
  return addressIsPOBox;
}

export function cleanString(val) {
  return Array.from(val)
    .reduce((prev, curr) => {
      if (
        curr.charCodeAt() <= 127 ||
        (curr.charCodeAt() >= 160 && curr.charCodeAt() <= 255)
      ) {
        prev.push(curr);
      }
      return prev;
    }, [])
    .join('');
}

export function setupInterceptors(axiosInstance) {
  const hostURLForSkip = `${publicRuntimeConfig.webregServiceURL}/api/v1`;
  // On outbound server-api requests, ensure that the keys all
  // are snake_cased
  const skipRenameUrls = [
    `${hostURLForSkip}/identity/answers/submit`,
    `${hostURLForSkip}/identity/challenge/submit`
  ];
  axiosInstance.interceptors.request.use(
    config => {
      if (
        config.data &&
        config.url.includes(`${hostURLForSkip}`) &&
        skipRenameUrls.indexOf(config.url) === -1
      ) {
        config.data = rename(config.data, key => Case.snake(key));
      }
      if (
        config.params &&
        config.url.includes(`${hostURLForSkip}`) &&
        skipRenameUrls.indexOf(config.url) === -1
      ) {
        config.params = rename(config.params, key => Case.snake(key));
      }
      return config;
    },
    err => Promise.reject(err)
  );

  // On inbound requests, ensure that all keys are
  // camelCased except for refetching feature toggles after login
  const skipCamelCasingPropertiesUrl = '/api/fetch-toggles';
  axiosInstance.interceptors.response.use(
    config => {
      let { data } = config;
      if (data && !config?.config?.url.includes(skipCamelCasingPropertiesUrl)) {
        if (typeof data === 'string') {
          data = JSON.parse(data);
        }
        config.data = rename(data, key => Case.camel(key));
      }
      return config;
    },
    err => Promise.reject(err)
  );

  // Handle 401's
  axiosInstance.interceptors.response.use(
    response => response,
    error => Promise.reject(error)
  );
}

export function removeQueryParams(url, keys) {
  let rtn = url.split('?')[0];
  let param;
  let paramsArr = [];
  const queryString = url.indexOf('?') !== -1 ? url.split('?')[1] : '';
  if (queryString !== '') {
    paramsArr = queryString.split('&');
    for (let i = paramsArr.length - 1; i >= 0; i -= 1) {
      [param] = paramsArr[i].split('=');
      if (keys.includes(param)) {
        paramsArr.splice(i, 1);
      }
    }
    if (paramsArr.length) rtn = `${rtn}?${paramsArr.join('&')}`;
  }
  return rtn;
}

// pathParams example
// [
//   {
//     path: '/reset-password/:type/:uid/:token', <- format same as react-router
//     paramsToRemove: ['uid', 'token']
//   }
// ]
export function removePathParams({ url, pathParams = [] }) {
  const urlPath = new URL(url).pathname;
  const matchedPath = pathParams.find(pathParam =>
    matchPath(urlPath, {
      path: pathParam.path
    })
  );

  if (matchedPath) {
    const match = matchPath(urlPath, {
      path: matchedPath.path
    });
    const urlWithoutPathParams = Object.keys(match.params)
      .filter(param => matchedPath.paramsToRemove.includes(param))
      .reduce(
        (acc, paramName) => `${acc}`.replace(`/${match.params[paramName]}`, ''),
        url
      );
    return urlWithoutPathParams;
  }

  return url;
}

export function sanitizeUrl({ url, queryParams = [], pathParams = [] }) {
  const urlMinusQueryParams = removeQueryParams(url, queryParams);
  const sanitizedUrl = removePathParams({
    url: urlMinusQueryParams,
    pathParams
  });
  return sanitizedUrl;
}

export function getAccessManagementRoutes(accessManagementEnabled) {
  if (accessManagementEnabled) {
    return {
      phoneCheck: `${ACCESS_MANAGEMENT_URL}/phone-check`,
      verify: `${ACCESS_MANAGEMENT_URL}/verify`,
      verifyVoice: `${ACCESS_MANAGEMENT_URL}/verify-voice`,
      verifyConfirm: `${ACCESS_MANAGEMENT_URL}/verify/confirm`
    };
  }
  return {
    phoneCheck: `${HOST_URL}/phone-check`,
    verify: `${HOST_URL}/access-request`,
    verifyVoice: `${HOST_URL}/access-request/voice-pin`,
    verifyConfirm: `${HOST_URL}/access-request/confirm`
  };
}

export const isApproverInCompletePlaceholder = preregisteredUser =>
  !!preregisteredUser &&
  preregisteredUser.role === 'approver' &&
  preregisteredUser.nextRegisterStep === 'complete_placeholder';

export const shouldValidatePlaceholderToken = (
  preregisteredUser,
  toggleEnabled
) => toggleEnabled && isApproverInCompletePlaceholder(preregisteredUser);

const NEXT_REDUX_STORE = 'NEXT_REDUX_STORE';
export const getIpAddress = () => {
  if (typeof window !== 'undefined' && window[NEXT_REDUX_STORE]) {
    return window[NEXT_REDUX_STORE].getState().app.ipAddress;
  }
  return null;
};

export const ModularReduxContext = React.createContext();
export const ReduxContext = React.createContext();
